import type { EmailListItem } from '@/types/email';
import type { EmailFiltersState } from '@/components/features/emails/email-filters';

/**
 * M-44 — Email-list scroll & state restoration (Gmail-style).
 *
 * A per-tab, in-memory snapshot of the `/emails` list so returning from an
 * opened email restores the loaded "Load more" pages, the active filters, and
 * the exact `window.scrollY`. A module singleton (NOT sessionStorage) gives
 * per-tab isolation and the intended "hard refresh / new tab = fresh from top"
 * behavior for free — the module reloads on a full page load.
 *
 * Single responsibility: hold / return / clear ONE snapshot (plus two
 * in-place field updaters). Deliberately React-free and `window`-free so it is
 * trivially unit-testable; the freshness (TTL) policy and all DOM access live
 * in `EmailsPage`, not here.
 *
 * B5 single-writer rule: `updateEmailListScrollY` is the ONLY writer of
 * `scrollY` at runtime (the page's rAF scroll listener owns it). The data
 * persist path preserves the existing `scrollY` rather than re-reading
 * `window.scrollY`, so a one-time clamped restore can't ratchet the saved
 * position toward the top across a session.
 */
export interface EmailListSnapshot {
  emails: EmailListItem[];
  cursor: string | undefined;
  total: number;
  filters: EmailFiltersState;
  scrollY: number;
  /** Active customer_id (from the list rows) — B7 cross-tenant guard. */
  customerId: string | null;
  /** The row the operator opened — restored to keyboard focus on return (WCAG 2.4.3). */
  lastOpenedEmailId: string | null;
  /** `Date.now()` at write time — the page ignores a snapshot older than SNAPSHOT_TTL_MS. */
  savedAt: number;
}

/** A snapshot older than this (since `savedAt`) is treated as stale → fresh from top. */
export const SNAPSHOT_TTL_MS = 5 * 60 * 1000; // 5 minutes

let snapshot: EmailListSnapshot | null = null;

export function saveEmailListSnapshot(next: EmailListSnapshot): void {
  snapshot = next;
}

export function getEmailListSnapshot(): EmailListSnapshot | null {
  return snapshot;
}

export function clearEmailListSnapshot(): void {
  snapshot = null;
}

/** B5 single-writer: mutate ONLY scrollY, in place. No-op when there is no snapshot. */
export function updateEmailListScrollY(scrollY: number): void {
  if (snapshot) snapshot.scrollY = scrollY;
}

/** Record the row the operator opened, for focus restoration. No-op when there is no snapshot. */
export function updateLastOpenedEmailId(id: string | null): void {
  if (snapshot) snapshot.lastOpenedEmailId = id;
}
